import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';

interface FAQItem {
  question: string;
  answer: string;
  category: 'general' | 'features' | 'pricing' | 'technical';
}

const faqData: FAQItem[] = [
  // General
  {
    question: "What is CodeRom?",
    answer: "CodeRom is an all-in-one dropshipping automation platform powered by AI. It helps you list products with GPT-4o optimized titles and descriptions, monitor prices and stock 24/7, manage orders, and automate your entire dropshipping business from a single dashboard.",
    category: "general"
  },
  {
    question: "Can I use CodeRom as a beginner?",
    answer: "Absolutely! CodeRom is designed to be user-friendly for beginners while powerful enough for experienced sellers. Our intuitive interface, step-by-step guides, and 24/7 support team will help you get started quickly. No technical knowledge required.",
    category: "general"
  },
  {
    question: "What marketplaces does CodeRom support?",
    answer: "Currently, CodeRom fully supports eBay with complete API integration including auto-listing, order sync, repricing, and professional templates. We're actively working on adding Shopify, Amazon, Walmart, TikTok Shop, and Etsy integrations - coming soon!",
    category: "general"
  },
  {
    question: "Which suppliers can I source from?",
    answer: "CodeRom supports Amazon UK, Amazon US, and Amazon DE as supplier sources with real-time price tracking, stock sync, and Prime detection. AliExpress integration is coming soon. You can source millions of products and list them on your eBay store automatically.",
    category: "general"
  },
  // Features
  {
    question: "How does AI product listing work?",
    answer: "Our AI-powered listing system uses GPT-4o to generate SEO-optimized titles and compelling descriptions for your products. It automatically detects the best eBay category (98% accuracy), extracts key features, and creates professional listings that convert. Just paste an Amazon URL and let AI do the rest.",
    category: "features"
  },
  {
    question: "Does CodeRom monitor price and stock for me?",
    answer: "Yes! CodeRom continuously monitors your supplier prices and stock levels 24/7. When prices change or items go out of stock, your eBay listings are automatically updated to protect your margins and prevent overselling. You can also set custom repricing rules.",
    category: "features"
  },
  {
    question: "Does CodeRom automate orders for me?",
    answer: "Order management features are coming soon! Currently, CodeRom helps you track orders, view order details, and manage your sales from one dashboard. Full order automation with auto-purchasing and tracking updates will be available in our upcoming release.",
    category: "features"
  },
  {
    question: "What are eBay Templates?",
    answer: "CodeRom includes 9 professionally designed eBay listing templates that make your products stand out. These mobile-responsive templates are optimized for conversions and give your store a professional look without any design skills needed.",
    category: "features"
  },
  {
    question: "How does the Smart Repricer work?",
    answer: "Our intelligent repricing system automatically adjusts your eBay prices based on supplier cost changes to maintain your profit margins. Set your minimum and maximum margins, and CodeRom handles the rest. You can also enable Safe Repricing to protect against sudden price spikes.",
    category: "features"
  },
  // Pricing
  {
    question: "Does CodeRom offer a free trial?",
    answer: "Yes! CodeRom offers a 14-day free trial with full access to all features. No credit card required to start. Explore the dashboard, list products, and see how CodeRom can transform your dropshipping business before committing.",
    category: "pricing"
  },
  {
    question: "How much does CodeRom cost after the trial?",
    answer: "After your free trial, CodeRom offers flexible pricing plans starting from affordable monthly subscriptions. Plans are based on the number of managed products and include all core features. Visit our pricing page for detailed plan comparisons.",
    category: "pricing"
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes, you can cancel your subscription at any time with no questions asked. There are no long-term contracts or cancellation fees. Your data remains accessible until the end of your billing period.",
    category: "pricing"
  },
  {
    question: "Can I manage multiple stores on CodeRom?",
    answer: "Yes! CodeRom supports multiple eBay stores from a single account. You can manage different stores, each with their own settings, templates, and pricing rules. Perfect for sellers with multiple niches or marketplaces.",
    category: "pricing"
  },
  // Technical
  {
    question: "Is CodeRom support available 24/7?",
    answer: "Yes, our dedicated support team is available around the clock to help you succeed. Get instant responses through live chat, or reach us via email. We're real people who care about your business - no bots!",
    category: "technical"
  },
  {
    question: "How do I connect my eBay store?",
    answer: "Connecting your eBay store takes just 2 minutes. Click 'Add Store' in your dashboard, select eBay, and authorize CodeRom through eBay's secure OAuth process. Once connected, you can start listing products immediately.",
    category: "technical"
  },
  {
    question: "Is my data secure with CodeRom?",
    answer: "Absolutely. We use industry-standard encryption and secure OAuth authentication for all marketplace connections. Your credentials are never stored - we only use secure tokens provided by eBay. Your business data is protected and private.",
    category: "technical"
  },
  {
    question: "What is Automatic Ordering (AO)?",
    answer: "AO is our Chrome Extension that automates Amazon checkout. When you receive an eBay order, AO handles the entire purchasing process — adding items to cart, entering the delivery address, validating prices, applying vouchers, and placing the order. It includes Subscribe & Save protection to prevent accidental subscriptions. Available on all plans.",
    category: "features"
  },
  {
    question: "What is Safe Repricing Protection?",
    answer: "Safe Repricing monitors your Amazon supplier prices. If a price increases by more than 20% suddenly, the system automatically sets your eBay listing to out-of-stock to prevent selling at a loss. Once the price stabilises for a configurable number of days, the listing is automatically restored. You can adjust thresholds and monitoring days in Settings.",
    category: "features"
  },
  {
    question: "Do you support multiple eBay marketplaces?",
    answer: "Yes. Coderom supports 8 eBay marketplaces: UK, US, Germany, France, Italy, Spain, Australia, and Canada. Each marketplace has its own Amazon source with localised pricing, currency conversion, and marketplace-specific eBay policies.",
    category: "general"
  },
  {
    question: "How does Auto Messages work?",
    answer: "Auto Messages automatically sends customisable buyer messages at two key moments: when a new order is received and when the item is shipped. You can enable or disable each trigger and customise the message templates in Settings. Available on all plans.",
    category: "features"
  },
  {
    question: "What does the Daily Email Report include?",
    answer: "Every morning you receive a personalised email summary with your sales figures, profit calculations, stock alerts, and out-of-stock warnings. Reports are timezone-aware — they arrive at 9 AM your local time.",
    category: "features"
  },
  {
    question: "How does Gmail Integration work?",
    answer: "Connect your Gmail account with an App Password in Settings → Email (2-Step Verification required). Coderom polls your inbox for Amazon dispatch emails and carrier tracking emails from Royal Mail and DPD. When a tracking number is detected, it is automatically matched to your eBay order and uploaded.",
    category: "features"
  },
  {
    question: "How many settings and controls does Coderom offer?",
    answer: "Coderom has 10 settings tabs with 120+ configurable options covering pricing rules, stock management, Amazon supplier filters, eBay policies, listing templates, buyer messages, safe repricing thresholds, auto ordering preferences, Gmail integration, and general platform settings.",
    category: "general"
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'general', label: 'General' },
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'technical', label: 'Technical' },
  ];

  const filteredFAQs = activeCategory === 'all'
    ? faqData
    : faqData.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // FAQPage structured data (first 10 questions — Google rich snippet limit)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.slice(0, 10).map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="FAQ — CodeRom Dropshipping Automation"
        description="Answers about CodeRom: AI listing, repricing, stock sync, safety & billing for eBay sellers."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />
    <div className="faq-page">
      {/* Header */}
      <section className="faq-header">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="faq-header-content"
          >
                        <h1>Frequently Asked Questions</h1>
            <p>Everything you need to know about CodeRom. Can't find what you're looking for? <a href="mailto:support@coderom.com">Contact our support team</a>.</p>
          </motion.div>
        </Container>
      </section>

      {/* Category Filter */}
      <section className="faq-filters">
        <Container>
          <div className="filter-buttons">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setOpenIndex(null);
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ List */}
      <section className="faq-list">
        <Container>
          <div className="faq-items">
            {filteredFAQs.map((faq, index) => (
              <motion.div
                key={index}
                className={`faq-item ${openIndex === index ? 'open' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <span>{faq.question}</span>
                  <div className="faq-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {openIndex === index ? (
                        <line x1="5" y1="12" x2="19" y2="12" />
                      ) : (
                        <>
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </>
                      )}
                    </svg>
                  </div>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      className="faq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="faq-cta">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Still have questions?</h2>
            <p>Our support team is here to help you 24/7</p>
            <div className="cta-buttons">
              <a href="mailto:support@coderom.com" className="btn-primary">
                Contact Support
              </a>
              <a href={`${process.env.REACT_APP_APP_URL || ''}/register`} className="btn-secondary">
                Start Free Trial
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      <style>{`
        .faq-page {
          min-height: 100vh;
          background: #f8fafc;
        }

        .faq-header {
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.72) 0%, rgba(51, 65, 85, 0.55) 100%),
                      url('https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&q=80') center/cover no-repeat;
          padding: 100px 0 60px;
          color: white;
        }

        .faq-header-content {
          max-width: 700px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 24px;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: white;
        }

        .faq-header h1 {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .faq-header p {
          font-size: 18px;
          opacity: 0.8;
          line-height: 1.6;
        }

        .faq-header p a {
          color: #f97316;
          text-decoration: none;
        }

        .faq-header p a:hover {
          text-decoration: underline;
        }

        .faq-filters {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 20px 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .filter-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-btn:hover {
          border-color: #cbd5e1;
          color: #334155;
        }

        .filter-btn.active {
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-color: transparent;
          color: white;
        }

        .faq-list {
          padding: 60px 0;
        }

        .faq-items {
          max-width: 800px;
          margin: 0 auto;
        }

        .faq-item {
          background: white;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }

        .faq-item:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .faq-item.open {
          border-color: #f97316;
          box-shadow: 0 4px 20px rgba(249, 115, 22, 0.15);
        }

        .faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          transition: color 0.2s;
        }

        .faq-item.open .faq-question {
          color: #f97316;
        }

        .faq-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          border-radius: 8px;
          flex-shrink: 0;
          transition: all 0.2s;
        }

        .faq-item.open .faq-icon {
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
        }

        .faq-answer {
          overflow: hidden;
        }

        .faq-answer p {
          padding: 0 24px 24px;
          margin: 0;
          color: #64748b;
          line-height: 1.7;
          font-size: 15px;
        }

        .faq-cta {
          padding: 80px 0;
          background: white;
        }

        .cta-content {
          text-align: center;
          max-width: 500px;
          margin: 0 auto;
        }

        .cta-content h2 {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
        }

        .cta-content p {
          color: #64748b;
          font-size: 18px;
          margin-bottom: 32px;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn-primary {
          padding: 14px 28px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          text-decoration: none;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.2s;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(249, 115, 22, 0.35);
          color: white;
        }

        .btn-secondary {
          padding: 14px 28px;
          background: white;
          color: #334155;
          text-decoration: none;
          font-weight: 600;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        @media (max-width: 768px) {
          .faq-header {
            padding: 80px 0 40px;
          }

          .faq-header h1 {
            font-size: 32px;
          }

          .faq-question {
            font-size: 15px;
            padding: 16px 20px;
          }

          .faq-answer p {
            padding: 0 20px 20px;
            font-size: 14px;
          }

          .cta-content h2 {
            font-size: 26px;
          }
        }
      `}</style>
    </div>
    <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default FAQPage;
